import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from "../utils/api";
import { useAlert } from '../components/AlertModal';
import { useAuthContext } from '../context/AuthContext';
import '../styles/SalesSummaryModal.css';

// 清算ボタンを表示するログインアカウント名（username）を指定。
// 例: new Set(['manager', 'staff01']);
// 空配列のままでは誰にも清算ボタンを表示しません。
const ALLOWED_CLOSING_USERNAMES = new Set(['pasomaru1f-master']);

const SalesSummaryModal = ({ closeModal, onClosingComplete, dataNow, timeNow, dayOfWeek, register }) => {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useAlert();
  const { user: userInfo } = useAuthContext();
  // 表示名ではなくログインに使う username で判定。
  const canCloseRegister = typeof userInfo?.username === 'string' &&
    ALLOWED_CLOSING_USERNAMES.has(userInfo.username);
  const [salesData, setSalesData] = useState(null);
  const [latestClosingDate, setLatestClosingDate] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // フォーマット処理は latestClosingDate が存在する時のみ
  const formattedDate = latestClosingDate?.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const weekday = latestClosingDate?.toLocaleString('ja-JP', { weekday: 'long' });
  const time = latestClosingDate?.toLocaleString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const finalFormattedDate = latestClosingDate ? `${formattedDate} ${weekday} ${time}` : '';

  useEffect(() => {
    async function fetchSalesSummary() {
      try {
        const data = await apiRequest(`inventory/sales_summary/?register_id=${register}`);
        setSalesData(data);
      } catch (error) {
        console.error('Error fetching sales summary:', error);
      }
    }
    fetchSalesSummary();
  }, [register]);

  useEffect(() => {
    async function fetchLatestClosingDate() {
      try {
        const response = await apiRequest(`inventory/register_closings/latest_closing_date_by_register/?register_id=${register}`);
        const dateStr = response.latest_closing_date || '2025/01/01';
        setLatestClosingDate(new Date(dateStr));
      } catch (error) {
        console.error('Error fetching latest closing date:', error);
        setLatestClosingDate(new Date('2025/01/01'));
      }
    }
    fetchLatestClosingDate();
  }, [register]);

  if (!salesData || !latestClosingDate) {
    return <div>読み込み中...</div>;
  }

  const formatCategoryName = (name) => name || "未設定";
  const sortByCategoryName = (arr) => arr.sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''));

  const sortedNormalSales = sortByCategoryName(salesData.normal_sales_by_category);
  const sortedTaxExemptSales = sortByCategoryName(salesData.tax_exempt_sales_by_category);

  // レジの現金確認 → 内訳入力 → B5精算表印刷へ移動
  const handleOpenDailyCashReport = () => {
    const now = new Date();
    const today = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
    ].join("-");
    closeModal();
    navigate(`/daily-cash-report?date=${today}&register_id=${register}`);
  };

  const handleRegisterClosing = async () => {

    // UIだけでなくクリック処理も制限（API側の権限確認も別途必要）
    if (!canCloseRegister || isClosing) return;

    setIsClosing(true);

    try {

      const isConfirmed = await showConfirm(
        "清算を実行してもよろしいですか？"
      );

      if (!isConfirmed) return;

      const payload = {
        register_id: register,
        latest_closing_date: latestClosingDate
          ? latestClosingDate.toISOString()
          : null,

        total_sales:
          parseInt(salesData.total_sales_all.replace(/[¥,]/g, '')) || 0,

        total_tax:
          parseInt(salesData.tax_amount.replace(/[¥,]/g, '')) || 0,

        total_discount:
          parseInt(salesData.discount_total.replace(/[¥,]/g, '')) || 0,

        total_transactions:
          parseInt(salesData.total_receipts, 10) || 0,

        total_items:
          parseInt(salesData.total_sales_count, 10) || 0,

        normal_sales_by_category:
          salesData.normal_sales_by_category.map(cat => ({
            category_id: cat.category_id,
            sales:
              parseInt(cat.total_sales.replace(/[¥,]/g, '')) || 0,
            transactions: cat.total_transactions || 0,
            cash_sales:
              parseInt(cat.cash.replace(/[¥,]/g, '')) || 0,
            cashless_sales:
              parseInt(cat.cashless.replace(/[¥,]/g, '')) || 0,
          })),

        tax_exempt_sales_by_category:
          salesData.tax_exempt_sales_by_category.map(cat => ({
            category_id: cat.category_id,
            sales:
              parseInt(cat.total_sales.replace(/[¥,]/g, '')) || 0,
            transactions: cat.total_transactions || 0,
            cash_sales:
              parseInt(cat.cash.replace(/[¥,]/g, '')) || 0,
            cashless_sales:
              parseInt(cat.cashless.replace(/[¥,]/g, '')) || 0,
          })),
      };


      // ========================================
      // ① 清算データ保存
      // ========================================

      const createdClosing = await apiRequest(
        "inventory/register/closing/",
        "POST",
        payload
      );

      console.log("清算保存完了:", createdClosing);


      // 日本時間の日付を取得
      // toISOString()だと午前0～9時に日付がずれるため使用しない
      const now = new Date();

      const today = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0")
      ].join("-");


      const printErrors = [];


      // ========================================
      // ② 清算レシート自動印刷（従来どおり）
      // ========================================

      try {

        const receiptData = await apiRequest(
          `inventory/register_closing/print/?date=${today}&register_id=${register}`,
          "GET"
        );

        if (window.electronAPI?.settlementPrint) {

          const result = await window.electronAPI.settlementPrint(
            receiptData
          );

          if (result?.success === false) {
            throw new Error(result.error || "清算レシート印刷失敗");
          }

        } else {

          console.warn("Electron清算印刷APIが見つかりません");

        }

      } catch (printError) {

        console.error(
          "清算レシート自動印刷エラー:",
          printError
        );

        printErrors.push("清算レシート");

      }


      // ========================================
      // ③ ジャーナル印刷確認
      // ========================================

      try {

        if (typeof onClosingComplete === "function") {

          // 取引履歴画面から開いた場合
          // 親画面で最新清算を取得し、印刷確認を表示
          await onClosingComplete(createdClosing);

        } else {

          // レジ画面から開いた場合
          // 親画面の処理がないため、ここで直接印刷確認

          const res = await apiRequest(
            `inventory/register_closing_list_for_print/?date=${today}&register_id=${register}`,
            "GET"
          );

          const closings = Array.isArray(res)
            ? res
            : (res?.results ?? []);

          // POSTの戻り値にIDがあれば優先
          const createdId =
            createdClosing?.id ??
            createdClosing?.closing_id ??
            createdClosing?.data?.id ??
            createdClosing?.closing?.id;

          const savedClosing = createdId
            ? closings.find(
                c => String(c.id) === String(createdId)
              )
            : null;

          // IDが返らないAPIの場合は最新の清算を使用
          const latestClosing = savedClosing ??
            [...closings].sort(
              (a, b) => Number(b.id) - Number(a.id)
            )[0];

          const closingId = latestClosing?.id;

          if (!closingId) {

            showAlert(
              "清算は完了しましたが、ジャーナル対象の清算が見つかりませんでした。"
            );

          } else {

            const printJournal = await showConfirm(
              "清算が完了しました。\n今回のジャーナルを印刷しますか？"
            );

            if (printJournal) {

              if (!window.electronAPI?.journalPrint) {

                showAlert(
                  "Electronのジャーナル印刷機能が利用できません。"
                );

              } else {

                const journalData = await apiRequest(
                  `inventory/journal/print/?closing_id=${closingId}`,
                  "GET"
                );

                const result =
                  await window.electronAPI.journalPrint(journalData);

                if (result?.success === false) {
                  throw new Error(
                    result.error || "ジャーナル印刷失敗"
                  );
                }

              }

            }

          }

        }

      } catch (journalError) {

        console.error(
          "清算後ジャーナル処理エラー:",
          journalError
        );

        printErrors.push("ジャーナル");

      }


      // ========================================
      // ④ 完了処理
      // ========================================

      if (printErrors.length > 0) {

        showAlert(
          `清算は完了しましたが、${printErrors.join("・")}の印刷に失敗しました。`
        );

      }

      // 清算画面を閉じる
      closeModal();


    } catch (error) {

      console.error("清算エラー:", error);

      const message =
        error?.details?.error ||
        "清算処理に失敗しました。もう一度お試しください。";

      showAlert(message);

    } finally {

      setIsClosing(false);

    }

  };
  // モーダルを閉じる
  const onCloserigicloseModal = (event) => {
    // クリックされたターゲットがモーダル本体（モーダル外）であることを確認
    if (event.target === event.currentTarget) {
      closeModal();  // モーダルを閉じる
    }
  };

  return (
    <div className="salesSummaryModal" onClick={onCloserigicloseModal}>
      <div className="salesSummaryModal__content">
        <h2>売上・入出金情報</h2>
        <p>前回の清算: {latestClosingDate ? `${finalFormattedDate}` : '読み込み中...'}</p>
        <p>現在の日時: {dataNow} {dayOfWeek} {timeNow}</p>

        <h3>カテゴリー別売上</h3>
        <div>
          <h4>通常</h4>
          <table className="salesSummaryModal__categoryTable">
            <thead>
              <tr>
                <th>カテゴリー</th>
                <th>点数/取引</th>
                <th>売上</th>
                <th>現金</th>
                <th>キャッシュレス</th>
              </tr>
            </thead>
            <tbody>
              {sortedNormalSales.map((category) => (
                <tr key={category.category_name || 'null'}>
                  <td>{formatCategoryName(category.category_name)}</td>
                  <td>{category.total_transactions} 点</td>
                  <td>{category.total_sales}</td>
                  <td>{category.cash}</td>
                  <td>{category.cashless}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>小計</strong></td>
                <td><strong>{sortedNormalSales.reduce((sum, category) => sum + category.total_transactions, 0)} 点 / {salesData.total_normal_receipts} 件</strong></td>
                <td><strong>{salesData.normal_total_sales}</strong></td>
                <td><strong>{salesData.normal_cash_total_without}</strong></td>
                <td><strong>{salesData.normal_cashless_total_without}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div>
          <h4>免税</h4>
          <table className="salesSummaryModal__categoryTable">
            <thead>
              <tr>
                <th>カテゴリー</th>
                <th>点数/取引</th>
                <th>売上</th>
                <th>現金</th>
                <th>キャッシュレス</th>
              </tr>
            </thead>
            <tbody>
              {sortedTaxExemptSales.map((category) => (
                <tr key={category.category_name || 'null'}>
                  <td>{formatCategoryName(category.category_name)}</td>
                  <td>{category.total_transactions} 点</td>
                  <td>{category.total_sales}</td>
                  <td>{category.cash}</td>
                  <td>{category.cashless}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>小計</strong></td>
                <td><strong>{sortedTaxExemptSales.reduce((sum, category) => sum + category.total_transactions, 0)} 点 / {salesData.total_tax_exempt_receipts} 件</strong></td>
                <td><strong>{salesData.tax_exempt_total_sales}</strong></td>
                <td><strong>{salesData.tax_exempt_cash_total_without}</strong></td>
                <td><strong>{salesData.tax_exempt_cashless_total_without}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <h3>売上合計</h3>
        <table className="salesSummaryModal__categoryTable">
          <tbody>
            <tr><td><strong>総売上計:</strong></td><td>{salesData.total_sales_all}</td></tr>
            <tr><td><strong>総値引き額:</strong></td><td>{salesData.discount_total} (通常：{salesData.normal_discount_total} 免税：{salesData.tax_exempt_discount_total})</td></tr>
            <tr><td><strong>消費税:</strong></td><td>{salesData.tax_amount} (現金：{salesData.cash_tax_amount} キャッシュレス：{salesData.cashless_tax_amount})</td></tr>
            <tr><td><strong>売上点数:</strong></td><td>{salesData.total_sales_count} 点 / {salesData.total_receipts} 件</td></tr>
            <tr><td><strong>平均単価:</strong></td><td>{salesData.average_unit_price}</td></tr>
          </tbody>
        </table>

        <h3>仕入れ</h3>
        <table className="salesSummaryModal__categoryTable">
          <tbody>
            <tr><td><strong>買取:</strong></td><td>{salesData.cash_purchase_payment}</td></tr>
            <tr><td><strong>仕入れ:</strong></td><td>{salesData.cash_purchase}</td></tr>
          </tbody>
        </table>

        <h3>入出金明細</h3>
        <table className="salesSummaryModal__categoryTable">
          <tbody>
            <tr><td><strong>入金:</strong></td><td>{salesData.cash_deposit}</td></tr>
            <tr><td><strong>出金:</strong></td><td>{salesData.cash_withdrawal}</td></tr>
            <tr><td><strong>経費:</strong></td><td>{salesData.cash_expenses}</td></tr>
            <tr><td><strong>返品:</strong></td><td>{salesData.cash_purchase}</td></tr>
            <tr><td><strong>現金決済額:</strong></td><td>{salesData.cash_total}</td></tr>
          </tbody>
        </table>

        <h3>現金残高</h3>
        <p><strong>現金残高:</strong> {salesData.cash_balance}</p>
        <div className='regiclose_button_area'>
          {canCloseRegister && (
            <button
              className="salesSummaryModal__button_regiclose"
              onClick={handleRegisterClosing}
              disabled={isClosing}
            >
              {isClosing ? "清算処理中..." : "清算"}
            </button>
          )}
          <button
            className="salesSummaryModal__button"
            onClick={handleOpenDailyCashReport}
          >
            精算表印刷
          </button>
          <button className="salesSummaryModal__button" onClick={closeModal}>閉じる</button>
        </div>
      </div>
    </div>
  );
};

export default SalesSummaryModal;
